import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import { formatCurrency } from '../utils/formatters';

interface Order {
  id: string;
  order_number: string;
  draft_number?: string;  // For draft orders
  total_amount: number;
  subtotal?: number;
  shipping_cost?: number;
  tax_amount?: number;
  status: string;
  payment_status: string;
  type: string;
}

interface FeeCalculation {
  base_amount: number;
  fee_amount: number;
  total_amount: number;
  fee_percentage: number;
}

const PaymentCheckout: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Parse payment type from query params
  const queryParams = new URLSearchParams(location.search);
  const paymentType = queryParams.get('payment_type') || 'ach';

  useEffect(() => {
    loadOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Removed fee calculation as Stripe will handle fees

  const loadOrderData = async () => {
    try {
      // Try to load as order first, fall back to draft endpoint for backward compatibility
      let response;
      try {
        response = await api.get(`/orders/${quoteId}`);
        console.log('Order data loaded:', response.data);
      } catch (err) {
        // Fall back to draft endpoint for backward compatibility
        response = await api.get(`/drafts/${quoteId}`);
        console.log('Draft data loaded:', response.data);
      }

      // Extract the order/draft object from the response
      const orderData = response.data.order || response.data.quote || response.data;
      setOrder(orderData);
      
      // Check if order can be paid
      if (orderData.payment_status === 'paid') {
        setError('This order has already been paid');
      } else if (orderData.type === 'QUOTE' && orderData.status && !['approved', 'quote'].includes(orderData.status.toLowerCase())) {
        setError('This draft is not yet approved for payment');
      }
    } catch (error) {
      console.error('Failed to load order:', error);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!order) return;

    // For ACH, skip showing processing spinner - treat as immediate
    if (paymentType !== 'ach') {
      setProcessing(true);
    }
    setError(null);

    try {
      const response = await api.post(`/payments/checkout/${order.id}`, {
        payment_method: paymentType === 'ach' ? 'ach' : 'card',
        notes: notes || undefined,
        include_convenience_fee: paymentType === 'credit_debit'
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error: any) {
      console.error('Checkout failed:', error);
      // Handle error as string or object
      let errorMessage = 'Failed to create checkout session';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Handle validation errors array
          errorMessage = error.response.data.detail
            .map((err: any) => err.msg || err.message || JSON.stringify(err))
            .join('. ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      }
      setError(errorMessage);
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !order) {
    return (
      <Layout>
        <div className="container py-5">
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/orders')}
          >
            Back to Orders
          </button>
        </div>
      </Layout>
    );
  }

  const isDesktop = windowWidth >= 992;

  return (
    <Layout>
      <div className="container-fluid" style={{ maxWidth: '1400px', padding: '20px' }}>
        {/* Page Header */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ color: '#2c3e50', fontWeight: '600', fontSize: '28px', marginBottom: '10px' }}>Payment Processing</h2>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: isDesktop ? 'nowrap' : 'wrap',
          gap: '30px'
        }}>
          {/* Order Summary Section - Left */}
          <div style={{
            flex: isDesktop ? '0 0 40%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(40% - 15px)' : '100%'
          }}>
            <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <div className="card-header" style={{
                backgroundColor: '#4D64C1',
                borderRadius: '7px 7px 0 0',
                padding: '15px 20px',
                borderBottom: '1px solid #dee2e6'
              }}>
                <h5 className="mb-0 text-white" style={{ fontSize: '18px', fontWeight: '600' }}>
                  <i className="fas fa-receipt me-2"></i>Order Summary
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {order && (
                  <>
                    <div className="bg-light rounded p-3 mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted" style={{ fontSize: '14px' }}>Order Number:</span>
                        <span className="fw-bold" style={{ fontSize: '16px', color: '#4D64C1' }}>
                          {order.order_number || order.draft_number}
                        </span>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    {(order.subtotal !== undefined && order.subtotal !== null) ? (
                      <div>
                        <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                          <span style={{ fontSize: '15px', color: '#6c757d' }}>Subtotal</span>
                          <span style={{ fontSize: '15px', fontWeight: '500' }}>{formatCurrency(order.subtotal)}</span>
                        </div>
                        {order.shipping_cost !== undefined && order.shipping_cost > 0 && (
                          <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                            <span style={{ fontSize: '15px', color: '#6c757d' }}>Shipping</span>
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{formatCurrency(order.shipping_cost)}</span>
                          </div>
                        )}
                        {order.tax_amount !== undefined && order.tax_amount > 0 && (
                          <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                            <span style={{ fontSize: '15px', color: '#6c757d' }}>Tax</span>
                            <span style={{ fontSize: '15px', fontWeight: '500' }}>{formatCurrency(order.tax_amount)}</span>
                          </div>
                        )}
                        {paymentType === 'credit_debit' && (
                          <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                              Convenience Fee (3%)
                              <i className="fas fa-info-circle text-muted ms-1"
                                 title="A 3% convenience fee applies to credit/debit card payments"
                                 style={{ fontSize: '12px', cursor: 'help' }}></i>
                            </span>
                            <span style={{ fontSize: '15px', fontWeight: '500', color: '#212529' }}>
                              {formatCurrency((order.total_amount || 0) * 0.03)}
                            </span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between align-items-center pt-4">
                          <span className="h5 mb-0" style={{ fontWeight: '600', color: '#2c3e50' }}>Total Amount</span>
                          <span className="h4 mb-0" style={{ fontWeight: '700', color: '#4D64C1' }}>
                            {formatCurrency(paymentType === 'credit_debit' ? order.total_amount * 1.03 : order.total_amount)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded p-4" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="h5 mb-0" style={{ color: '#2c3e50' }}>Total Amount:</span>
                          <span className="h4 mb-0 fw-bold" style={{ color: '#4D64C1' }}>{formatCurrency(order.total_amount)}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Payment Information Section - Right */}
          <div style={{
            flex: isDesktop ? '0 0 60%' : '0 0 100%',
            maxWidth: isDesktop ? 'calc(60% - 15px)' : '100%'
          }}>
            <div className="card" style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <div className="card-header" style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '7px 7px 0 0',
                padding: '15px 20px',
                borderBottom: '1px solid #dee2e6'
              }}>
                <h5 className="mb-0" style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50' }}>
                  <i className="fas fa-credit-card me-2"></i>Payment Information
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {order && (
                  <>
                    {error && (
                      <div className="alert alert-danger mb-4" role="alert">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                      </div>
                    )}

                    {!error && (
                      <>
                        {/* Payment Methods */}
                        <div className="mb-4">
                          <h6 className="mb-3" style={{ color: '#2c3e50', fontWeight: '600' }}>Payment Method Selected</h6>
                          <div className="bg-light rounded p-3">
                            {paymentType === 'ach' ? (
                              <div>
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-university text-success me-2" style={{ fontSize: '20px' }}></i>
                                  <strong>ACH Bank Transfer</strong>
                                  <span className="badge bg-success ms-2">No Fee</span>
                                </div>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                  Direct bank transfer with no convenience fee.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <div className="d-flex align-items-center mb-2">
                                  <i className="fas fa-credit-card text-primary me-2" style={{ fontSize: '20px' }}></i>
                                  <strong>Credit/Debit Card</strong>
                                  <span className="badge bg-warning ms-2">3% Fee</span>
                                </div>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                  Visa, Mastercard, American Express, Discover accepted.
                                  <br />
                                  <small style={{ color: '#212529' }}>A 3% convenience fee will be added to your total.</small>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment Amount */}
                        <div className="mb-4">
                          <div className="border rounded p-4">
                            <div className="d-flex flex-column">
                              {paymentType === 'credit_debit' && (
                                <>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>Order Total:</span>
                                    <span style={{ fontSize: '16px' }}>
                                      {formatCurrency(order.total_amount)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span style={{ fontSize: '14px', color: '#dc3545' }}>Convenience Fee (3%):</span>
                                    <span style={{ fontSize: '16px', color: '#dc3545' }}>
                                      +{formatCurrency(order.total_amount * 0.03)}
                                    </span>
                                  </div>
                                  <hr className="my-2" />
                                </>
                              )}
                              <div className="d-flex justify-content-between align-items-center">
                                <span style={{ fontSize: '16px', color: '#6c757d' }}>Total Amount Due:</span>
                                <span className="h4 mb-0 fw-bold" style={{ color: '#2c3e50' }}>
                                  {formatCurrency(paymentType === 'credit_debit' ? order.total_amount * 1.03 : order.total_amount)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Payment Notes */}
                        <div className="mb-4">
                          <label className="form-label" style={{ fontWeight: '500', color: '#495057' }}>
                            <i className="fas fa-sticky-note me-2"></i>
                            Payment Notes (Optional)
                          </label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Add any reference number or special instructions..."
                            style={{ borderRadius: '8px' }}
                          />
                        </div>

                        {/* Payment Processor Notice */}
                        <div className="alert alert-light mb-4">
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-2"></i>
                            Payment processing handled securely by Stripe.
                          </small>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-grid gap-3">
                          <button
                            className="btn btn-lg"
                            onClick={handleCheckout}
                            disabled={processing}
                            style={{
                              backgroundColor: '#4D64C1',
                              color: 'white',
                              border: 'none',
                              padding: '12px',
                              fontSize: '16px',
                              fontWeight: '500',
                              borderRadius: '6px'
                            }}
                          >
                            {processing ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Processing Payment...
                              </>
                            ) : (
                              <>
                                Proceed to Payment
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-lg"
                            onClick={() => navigate(order?.type === 'QUOTE' ? `/drafts/${quoteId}` : `/orders/${quoteId}`)}
                            disabled={processing}
                            style={{ padding: '12px', fontSize: '16px', borderRadius: '6px' }}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentCheckout;